
/**
 * Retrieves and aggregates project data, options, and progress claims for budget preview and invoice generation.
 *
 * @param {Object} params
 * @param {string|number} params.projectId - The unique identifier for the project.
 * @param {string|number} [params.progressClaimId] - The ID of the progress claim (optional).
 * @param {Function} params.setProjectDataAndOptionData - Callback to set project and option data in state.
 * @param {Function} params.dispatch - Redux dispatch function for async actions.
 * @returns {Promise<Object>} Aggregated project data, options, claims, and due date for use in budget preview and invoice flows.
 *
 * Use case: Used to fetch, aggregate, and prepare all project, stage, and option data for budget preview, claim handling, and invoice generation in the project execution dashboard.
 */

import _ from 'lodash';
import {
  getOptions,
  getProgressClaimList,
  getProjectSummary,
} from '../../../../../../actions/project-management.action';
import idx from 'idx';
import { generateVariationNumber } from '../../../../../../util/common';

/**
 * Sets allocated cost fields on a task object for material, labor, subcontractor, and profit margin.
 * @param {Object} task - The task or option object to update.
 * @returns {Object} The updated task with allocated cost fields.
 *
 * Use case: Used to ensure all cost fields are present and initialized for downstream calculations and UI display.
 */
export const setAllocatedCost = (task) => {
  task.allocatedMaterialCost =
    idx(task, (_task) => _task.materialTotal_SaleAmount) || 0;
  task.allocatedLaborCost =
    idx(task, (_task) => _task.laborTotal_SaleAmount) || 0;
  task.allocatedSubContractorCost =
    idx(task, (_task) => _task.subcontractorTotal_SaleAmount) || 0;
  task.allocatedProfitMarginCost =
    idx(task, (_task) => _task.profitMarginCost) || 0;
  task.remainingPercentage = 0;
  task.remainingAmount = 0;
  task.claimRemainingAmount = false;
  return task || {};
};

/**
 * Creates a section-level claim entry for attributed sections (is_attribution_level=true).
 * Aggregates all child task costs and approved variations into a single claimable item.
 * 
 * @param {Object} stage - The stage/section object with is_attribution_level=true
 * @param {Array} variations - Optional array of approved variations for this section
 * @returns {Object} Section claim entry with aggregated costs and proper type
 */
export const createSectionClaimEntry = (stage, variations = [], progressClaims = [], progressClaimId = "") => {
  // Recursively aggregate costs from all child tasks
  let totalMaterialCost = 0;
  let totalLaborCost = 0;
  let totalSubContractorCost = 0;
  let totalProfitMarginCost = 0;

  const aggregateTaskCosts = (stages) => {
    if (!stages) return;

    for (const childStage of (Array.isArray(stages) ? stages : [])) {
      if (Array.isArray(childStage.stages)) {
        aggregateTaskCosts(childStage.stages);
      }
      if (Array.isArray(childStage.tasks)) {
        for (const task of childStage.tasks) {
          totalMaterialCost += parseFloat(idx(task, (_) => _.materialTotal_SaleAmount) || 0);
          totalLaborCost += parseFloat(idx(task, (_) => _.laborTotal_SaleAmount) || 0);
          totalSubContractorCost += parseFloat(idx(task, (_) => _.subcontractorTotal_SaleAmount) || 0);
          totalProfitMarginCost += parseFloat(idx(task, (_) => _.profitMarginCost) || 0);
        }
      }
    }
  };

  // Collect all stage IDs in the hierarchy (for variation matching and history lookup)
  const collectStageIds = (stageData, ids = []) => {
    ids.push(stageData.id);
    if (Array.isArray(stageData.stages)) {
      stageData.stages.forEach((s) => collectStageIds(s, ids));
    }
    return ids;
  };
  const hierarchyStageIds = collectStageIds(stage);

  // If stage has nested stages, aggregate from them
  if (Array.isArray(stage.stages)) {
    aggregateTaskCosts(stage.stages);
  }
  // Also aggregate from direct tasks
  if (Array.isArray(stage.tasks)) {
    stage.tasks.forEach((task) => {
      totalMaterialCost += parseFloat(idx(task, (_) => _.materialTotal_SaleAmount) || 0);
      totalLaborCost += parseFloat(idx(task, (_) => _.laborTotal_SaleAmount) || 0);
      totalSubContractorCost += parseFloat(idx(task, (_) => _.subcontractorTotal_SaleAmount) || 0);
      totalProfitMarginCost += parseFloat(idx(task, (_) => _.profitMarginCost) || 0);
    });
  }

  // Add approved variations under this section hierarchy
  if (Array.isArray(variations)) {
    variations.forEach((variation) => {
      // Check if variation belongs to this section or its children
      if (hierarchyStageIds.includes(variation.stageId)) {
        // Include non-REJECTED/CANCELLED selected variations
        if (!['REJECTED', 'CANCELLED'].includes(variation.proposal_status) && variation.proposal_selected === true) {
          totalMaterialCost += parseFloat(idx(variation, (_) => _.materialTotal_SaleAmount) || 0);
          totalLaborCost += parseFloat(idx(variation, (_) => _.laborTotal_SaleAmount) || 0);
          totalSubContractorCost += parseFloat(idx(variation, (_) => _.subcontractorTotal_SaleAmount) || 0);
          totalProfitMarginCost += parseFloat(idx(variation, (_) => _.profitMarginCost) || 0);
        }
      }
    });
  }

  const totalBefore = totalMaterialCost + totalLaborCost + totalSubContractorCost;

  // Calculate historical claims for this section hierarchy
  let previousClaimAmount = 0;
  if (Array.isArray(progressClaims)) {
    progressClaims.forEach((claim) => {
      if (claim.u_id !== progressClaimId && Array.isArray(claim.progressClaimTask)) {
        const matchingEntries = claim.progressClaimTask.filter(t =>
          !t.isDelete &&
          (hierarchyStageIds.includes(t.stageId) || hierarchyStageIds.includes(t.id))
        );

        if (matchingEntries.length > 0) {
          // Prefer section-level claims if they exist in this history record
          const sectionClaims = matchingEntries.filter(t => t.type === 'SECTION');
          const toSum = sectionClaims.length > 0 ? sectionClaims : matchingEntries;
          toSum.forEach(t => previousClaimAmount += parseFloat(t.total || 0));
        }
      }
    });
  }

  const previousPercentageComplete = totalBefore > 0 ? (previousClaimAmount / totalBefore) * 100 : 0;

  return {
    id: stage.id,
    stageId: stage.id,
    name: stage.name,
    description: stage.name,
    type: 'SECTION',
    // Aggregated costs (including variations)
    allocatedMaterialCost: totalMaterialCost,
    allocatedLaborCost: totalLaborCost,
    allocatedSubContractorCost: totalSubContractorCost,
    allocatedProfitMarginCost: totalProfitMarginCost,
    materialTotal_SaleAmount: totalMaterialCost,
    laborTotal_SaleAmount: totalLaborCost,
    subcontractorTotal_SaleAmount: totalSubContractorCost,
    profitMarginCost: totalProfitMarginCost,
    totalBefore: totalBefore,
    sub_total: totalBefore,
    // Claim values
    remainingPercentage: 0,
    remainingAmount: 0,
    previousPercentageComplete: previousPercentageComplete,
    previousClaimAmount: previousClaimAmount,
    percentageComplete: previousPercentageComplete,
    claimRemainingAmount: false,
    isSelected: false
  };
};

/**
 * Loads all project, stage, and claim data, and prepares selected tasks and options for UI and calculation.
 */
export const getProjectData = async ({
  projectId,
  progressClaimId,
  setProjectDataAndOptionData,
  dispatch,
}) => {
  // Initialize local variables for project, options, claims, and stages
  let optionData = [];
  let projectData = {};
  let selectedTask = [];
  let sortedtempArray = [];
  let customerPaymentTermDays = 0;
  let progressClaims = [];
  let stages = [];
  let hasStages = false;
  let projectQuoteType = '';
  const dueDate = new Date();

  if (projectId) {
    // Fetch project summary, options, and progress claims from API via Redux actions
    const project = await dispatch(getProjectSummary(projectId)).catch((e) =>
      console.log(e)
    );
    optionData = await dispatch(getOptions(projectId, { type: 'invoice' })).catch((e) =>
      console.log(e)
    );
    progressClaims = await dispatch(getProgressClaimList(projectId)).catch(
      (e) => console.log(e)
    );
    // await getLaborCost(projectId);
    // await getLevels(project.level);

    // Extract key project fields for summary and UI
    projectQuoteType = project.quoteType;
    customerPaymentTermDays = project.customer
      ? project.customer.paymentTerms
      : 0;

    stages = project.stages || [];
    hasStages = project.hasStages;

    projectData = {
      projectId: project.id,
      projectName: project.name,
      projectDescription: project.description,
      customerName: project.customer && project.customer.businessName,
      hasStages: project.hasStages,
      projectType: project.type,
      projectQuoteType: project.quoteType,
      customerLevel: project.customer ? project.customer.level : '',
      levelId: project.level,
      projectStages: project.stages,
      projectDataType: project.projectType,
      autoCalculateRetention: project.autoCalculateRetention,
      maxRetentionPercentage: project.maxRetentionPercentage,
      defaultRetentionPercentage: project.defaultRetentionPercentage,
    };

    /**
     * Traverse all project stages and collect all tasks for selection and cost allocation.
     * Each task is initialized with cost fields and marked as not selected.
     * For attributed sections (is_attribution_level=true), create section-level claim entries instead of task-level.
     */
    if (project.stages && Array.isArray(project.stages)) {
      const traverseStages = (stages) => {
        for (const stage of stages) {
          // Check if this section has attribution level set (for section-level claims)
          const isAttributionLevel = stage.is_attribution_level === true || stage.lock_flag === true;

          if (isAttributionLevel) {
            // For attributed sections, create a section-level claim entry
            // This aggregates all child task data + variations into a single claimable row
            const sectionClaimEntry = createSectionClaimEntry(stage, optionData, progressClaims, progressClaimId);
            selectedTask.push(sectionClaimEntry);
            // Don't traverse children of attributed sections - they're hidden
            continue;
          }

          if (Array.isArray(stage.stages)) {
            traverseStages(stage.stages);
          } else if (Array.isArray(stage.tasks)) {
            stage.tasks.forEach((task) => {
              task.isSelected = false;
              const allocatedCost = setAllocatedCost(task);
              selectedTask.push({ ...allocatedCost, type: 'TASK', stageId: stage.id });

              // if (task.subTasks && Array.isArray(task.subTasks)) {
              //     task.subTasks.forEach((subTask) => {
              //         subTask.isSelected = false;
              //         const allocatedCost = setAllocatedCost(subTask);
              //         selectedTask.push({ ...allocatedCost, type: "SUBTASK" });
              //     });
              // }
            });
          }
        }
      };
      traverseStages(project.stages);
    }

    /**
     * Prepare and group option data by revision, sort by creation date, and assign variation numbers.
     * Also, collect approved and selected variations for claim handling.
     */
    let tempArray = [];
    optionData.forEach((option) => {
      option.revisionNumber =
        idx(option, (_) => _.taskRevision[0].RevisionNumber) || '';
      option.revisionId =
        idx(option, (_) => _.taskRevision[0].RevisionId) || '';
      option.revisionName =
        idx(option, (_) => _.taskRevision[0].revisionName) || '';
      option.subTasks = option.subTasks || [];
      tempArray.push(option);
    });

    sortedtempArray = _.chain(tempArray)
      .groupBy('revisionId')
      .map((revisions, revisionId) => {
        const sortedRevisions =
          _.sortBy(revisions, [(o) => new Date(o.createdAt || null)]) || [];
        return { revisionId, revisions: sortedRevisions };
      })
      .sortBy((o) => o.revisions[0].createdAt)
      .map((data) => data.revisions)
      .value()
      .flat(1);

    sortedtempArray.forEach((option, optionIndex) => {
      const index = _.findIndex(
        _.uniqBy(sortedtempArray, 'revisionId'),
        (item) => item.id === option.id
      );
      if (index !== -1) option.revision = true;
      option.variationNo = generateVariationNumber(optionIndex + 1, 3);
      if (
        !['REJECTED', 'CANCELLED'].includes(option.proposal_status)
      ) {
        option.isSelected = !!option.proposal_selected;
        const allocatedCost = setAllocatedCost(option);
        selectedTask.push({ ...allocatedCost, type: 'VARIATION' });
      }
    });
  }

  // Calculate due date based on customer payment terms if no progress claim is selected
  if (!progressClaimId)
    dueDate.setDate(dueDate.getDate() + (customerPaymentTermDays || 0));

  // Return all aggregated and prepared data for use in budget preview and invoice flows
  return {
    projectId,
    projectData,
    selectedTask,
    progressClaims,
    stages,
    hasStages,
    optionData: sortedtempArray,
    projectQuoteType,
    dueDate,
  };
};

/**
 * Combines project stage data and option (variation) data into a single structure for UI rendering.
 * @param {Object} params
 * @param {Array} params.stagesData - Array of stage objects from the project.
 * @param {Array} params.optionData - Array of option/variation objects.
 * @returns {Array} Combined stage and variation data for rendering in budget preview tables.
 *
 * Use case: Used to merge variations into the correct project stages for display in budget and invoice tables.
 */
export const combineProjectData = ({ stagesData = [], optionData = [] }) => {
  const sectionData = _.cloneDeep(stagesData);
  const variations = optionData;

  // Recursively traverse stages and append matching variations to their respective stages
  const traverseStages = (stages) => {
    if (!stages) return null;

    return stages.map((stage) => {
      let updatedStage = { ...stage };
      if (updatedStage.stages && Array.isArray(updatedStage.stages)) {
        updatedStage.stages = traverseStages(updatedStage.stages);
      } else {
        const matchingVariations = variations.filter(
          (variation) => variation.stageId === updatedStage.id
        );
        if (matchingVariations.length > 0) {
          if (!updatedStage.tasks) updatedStage.tasks = [];
          updatedStage.tasks.push(...matchingVariations);
        }
      }
      return updatedStage;
    });
  };

  // Return the fully combined stage and variation structure
  return traverseStages(sectionData);
};
